/**
 * SITE ITEM LABELS (2026-10-20, Matt): what a site plan calls each piece of site equipment and
 * site work by — "trash cans, BBQ, AC condensers, Pools, Spas, Fire pits, electrical
 * panels/meters (exterior placed), water meters, gas meters, etc." — when it sits outside the
 * building's walls (bim-geometry siteOutdoor.ts decides that). ONE table, so the site plan's
 * label and the site plan's visibility (viewTypes.ts `showOutdoorItems`) cannot disagree.
 *
 * Plain item names, nothing extra (Matt: "keep the notes simple and to the point"); the site
 * plan's Note 1 says everything is new unless indicated, so a new item carries no (N) — only an
 * existing one reads "(E) …". Furniture (umbrellas, chaises, trampolines) is FF&E shown for
 * information and is never labeled. Subpanels are interior equipment and are never labeled.
 */
import type { ResolvedElement } from './params'
import { isGateOp } from './categories/openingOps'

/** object kind → site plan label */
export const SITE_LABEL_OBJECT_KINDS: Record<string, string> = {
  condenser: 'A/C CONDENSER', miniSplitOutdoor: 'MINI-SPLIT CONDENSER', poolEquipment: 'POOL EQUIPMENT',
  waterHeater: 'WATER HEATER', tanklessWaterHeater: 'TANKLESS WATER HEATER', boiler: 'BOILER',
  outdoorGrill: 'BBQ', firePit: 'FIRE PIT', fountain: 'FOUNTAIN', trashCart: 'TRASH CART',
  gasMeter: 'GAS METER', waterMeterBox: 'WATER METER',
}

/** fixture kind → site plan label (exterior electrical equipment) */
export const SITE_LABEL_FIXTURE_KINDS: Record<string, string> = {
  panel: 'ELECTRICAL PANEL', meter: 'METER/MAIN', evCharger: 'EV CHARGER', inverter: 'SOLAR INVERTER', disconnect: 'DISCONNECT',
}

/** a meter/main's service size choices (amps) — the instance's `serviceAmps` */
export const SERVICE_AMP_CHOICES = ['100', '125', '150', '200', '225', '320', '400', '600'] as const
export const DEFAULT_SERVICE_AMPS = '200'

/** the plural a group of nearby items of one kind reads as (three trash carts in a row) —
 *  a kind not listed adds an S */
export const SITE_LABEL_PLURALS: Record<string, string> = {
  trashCart: 'TRASH CARTS', gasMeter: 'GAS METERS', waterMeterBox: 'WATER METERS', firePit: 'FIRE PITS', outdoorGrill: 'BBQS', condenser: 'A/C CONDENSERS', miniSplitOutdoor: 'MINI-SPLIT CONDENSERS' }

/** is this element a kind a site plan labels (inside or outside — the caller tests that) */
export function isSiteLabelKind(el: Pick<ResolvedElement, 'category' | 'typeParams'>): boolean {
  if (el.category === 'object') return !!SITE_LABEL_OBJECT_KINDS[String(el.typeParams.objectKind ?? '')]
  if (el.category === 'fixture') return !!SITE_LABEL_FIXTURE_KINDS[String(el.typeParams.fixtureKind ?? '')]
  // GATES (2026-10-20, Matt: "add gates to the list") — a gate in a fence or site wall
  if (el.category === 'door') return isGateOp(el.typeParams.operation as string)
  return el.category === 'pool'
}

/** the kind key a site label groups by (`object:trashCart`, `pool:spa` …) */
export function siteLabelKindKey(el: Pick<ResolvedElement, 'category' | 'typeParams' | 'params'>): string | null {
  if (el.category === 'object') return `object:${String(el.typeParams.objectKind ?? '')}`
  if (el.category === 'fixture') return `fixture:${String(el.typeParams.fixtureKind ?? '')}`
  if (el.category === 'pool') return `pool:${poolKindOf(el)}`
  if (el.category === 'door') return 'door:gate'
  return null
}

function poolKindOf(el: Pick<ResolvedElement, 'typeParams' | 'params'>): string {
  return String(el.params.poolKind ?? el.typeParams.poolKind ?? 'pool') === 'spa' ? 'spa' : 'pool'
}

/** a meter/main's service amps (the instance value, else the standard 200) */
export function serviceAmpsOf(el: Pick<ResolvedElement, 'params' | 'typeParams'>): string {
  const v = String(el.params.serviceAmps ?? el.typeParams.serviceAmps ?? '').replace(/[^0-9]/g, '')
  return v || DEFAULT_SERVICE_AMPS
}

/** the plain label text, or null when the element is not a labeled site item */
export function siteItemLabelText(el: Pick<ResolvedElement, 'category' | 'typeParams' | 'params'>, plural = false): string | null {
  if (el.category === 'pool') return poolKindOf(el) === 'spa' ? 'SPA' : 'POOL'
  if (el.category === 'door') return isGateOp(el.typeParams.operation as string) ? (plural ? 'GATES' : 'GATE') : null
  if (el.category === 'fixture') {
    const k = String(el.typeParams.fixtureKind ?? '')
    if (k === 'meter') return `${serviceAmpsOf(el)}A ELECTRICAL METER/MAIN`
    const t = SITE_LABEL_FIXTURE_KINDS[k]
    return t ? (plural ? `${t}S` : t) : null
  }
  if (el.category === 'object') {
    const k = String(el.typeParams.objectKind ?? '')
    const t = SITE_LABEL_OBJECT_KINDS[k]
    if (!t) return null
    return plural ? (SITE_LABEL_PLURALS[k] ?? `${t}S`) : t
  }
  return null
}
